import { FUNDING_STATUSES, type FundingStatus, type ProjectListItem, type Stage } from '../../api/types'
import { FUNDING_LABELS } from '../../lib/format'
import { daysUntilFollowUp, isActive } from '../focus/ranking'

// All Dashboard numbers come from these functions, applied to one filtered list,
// so every tile, chart and table always agrees.

export type DashboardFilters = {
  state: string | null
  category: string | null
  departmentId: number | null
  funding: FundingStatus | null
  createdById: number | null
}

export const NO_FILTERS: DashboardFilters = {
  state: null,
  category: null,
  departmentId: null,
  funding: null,
  createdById: null,
}

export function countActiveFilters(f: DashboardFilters): number {
  return Object.values(f).filter((v) => v !== null).length
}

/** Archived projects never count on the Dashboard. */
export function applyDashboardFilters(projects: ProjectListItem[], f: DashboardFilters): ProjectListItem[] {
  return projects.filter(
    (p) =>
      !p.isArchived &&
      (f.state === null || p.state === f.state) &&
      (f.category === null || p.category === f.category) &&
      (f.departmentId === null || p.departmentId === f.departmentId) &&
      (f.funding === null || p.fundingStatus === f.funding) &&
      (f.createdById === null || p.createdById === f.createdById),
  )
}

const sum = (list: ProjectListItem[]) => list.reduce((total, p) => total + p.valueRupees, 0)

export type Total = { value: number; count: number }

export function totalOf(list: ProjectListItem[]): Total {
  return { value: sum(list), count: list.length }
}

export function stageTotal(projects: ProjectListItem[], stage: Stage): Total {
  return totalOf(projects.filter((p) => p.stage === stage))
}

/** A labelled number for a bar or column. */
export type Bucket = { label: string; value: number; count: number; highlight?: boolean }

/**
 * "Follow-ups coming up": active projects by days until their next follow-up.
 * Today counts as day 1, so "Next 7 days" is today plus the following 6.
 */
export function followUpBuckets(projects: ProjectListItem[], today: Date): Bucket[] {
  const buckets: Bucket[] = [
    { label: 'Overdue', value: 0, count: 0, highlight: true },
    { label: 'Next 7 days', value: 0, count: 0 },
    { label: 'Days 8–14', value: 0, count: 0 },
    { label: 'Days 15–28', value: 0, count: 0 },
    { label: 'Later', value: 0, count: 0 },
    { label: 'No date', value: 0, count: 0 },
  ]
  for (const p of projects.filter(isActive)) {
    const days = daysUntilFollowUp(p, today)
    const index = !p.nextFollowUp ? 5 : p.isOverdue ? 0 : days < 7 ? 1 : days < 14 ? 2 : days < 28 ? 3 : 4
    buckets[index].count++
    buckets[index].value += p.valueRupees
  }
  return buckets
}

/** "Funding status": active pipeline value and count for each funding status. */
export function fundingBreakdown(projects: ProjectListItem[]): Bucket[] {
  const active = projects.filter(isActive)
  return FUNDING_STATUSES.map((status) => {
    const group = active.filter((p) => p.fundingStatus === status)
    return { label: FUNDING_LABELS[status], value: sum(group), count: group.length }
  })
}

/** "Value by state": biggest first; anything past the first `max` rows folds into "Other". */
export function valueByState(projects: ProjectListItem[], max = 7): Bucket[] {
  const byState = new Map<string, ProjectListItem[]>()
  for (const p of projects.filter(isActive)) {
    const key = p.state ?? 'Not set'
    byState.set(key, [...(byState.get(key) ?? []), p])
  }
  const rows = [...byState].map(([label, group]) => ({ label, value: sum(group), count: group.length }))
  rows.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
  if (rows.length <= max + 1) return rows

  const rest = rows.slice(max)
  return [
    ...rows.slice(0, max),
    {
      label: `Other (${rest.length} more)`,
      value: rest.reduce((t, r) => t + r.value, 0),
      count: rest.reduce((t, r) => t + r.count, 0),
    },
  ]
}

/** "Top 10 by value": active projects only. */
export function topByValue(projects: ProjectListItem[], n = 10): ProjectListItem[] {
  return projects
    .filter(isActive)
    .sort((a, b) => b.valueRupees - a.valueRupees)
    .slice(0, n)
}

/** "Follow-ups: overdue & next 14 days": soonest first. */
export function dueSoon(projects: ProjectListItem[], today: Date): ProjectListItem[] {
  return projects
    .filter((p) => isActive(p) && p.nextFollowUp && (p.isOverdue || daysUntilFollowUp(p, today) < 14))
    .sort((a, b) => (a.nextFollowUp! < b.nextFollowUp! ? -1 : a.nextFollowUp! > b.nextFollowUp! ? 1 : 0))
}
